import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Badge, Box, Toolbar, useMediaQuery } from '@mui/material'
import { useNavigate } from 'react-router'
import MenuIcon from '@mui/icons-material/Menu'
import logo from '../../assets/icons/short-logo.svg'
import AppRouteNames from '../../constants/route'
import Greeting from '../Greeting/Greeting'
import DrSecDim from '../DrSecDim'
import { OpenIDContext } from '../../api/openid'
import {
	HeaderLink,
	HeaderExLink,
	StyledAppBar,
	AppLogoExternal,
} from './HeaderStyles'
import { generatePath } from '../../shared/UrlUtils/urlUtils'
import Tooltip from '../../shared/Tooltip/Tooltip'
import TrialOffer from './components/TrialOffer/TrialOffer'
import * as Styled from './HeaderStyles'

// Lifts Dr. SecDim's trigger out of flow and pins it to the header's true centre.
// Applied only above 900px; at/below that it stays in-flow in the right cluster.
const CENTERED_SX = {
	position: 'absolute',
	left: '50%',
	top: '50%',
	transform: 'translate(-50%, -50%)',
} as const

const Header = () => {
	const [isOpen, setIsOpen] = useState(false)
	const [isTrialDialogOpen, setIsTrialDialogOpen] = useState(false)
	const [hasViewedTrialOffer, setHasViewedTrialOffer] = useState(
		localStorage.getItem('hasViewedTrialOffer') === 'true',
	)
	const menu = useRef<HTMLDivElement | null>(null)
	const smSize = useMediaQuery('(max-width: 600px)')
	// At/below this width the absolutely-centred brain icon starts crowding the nav
	// and right-hand controls, so it docks into the right cluster instead.
	const dockDrSecDimRight = useMediaQuery('(max-width: 900px)')
	const navigate = useNavigate()
	const { profile } = useContext(OpenIDContext)

	const handleClick = useCallback((e: MouseEvent) => {
		if (
			menu.current &&
			e.target instanceof Node &&
			!menu.current.contains(e.target)
		) {
			setIsOpen(false)
		}
	}, [])

	useEffect(() => {
		document.addEventListener('mousedown', handleClick)
		return () => {
			document.removeEventListener('mousedown', handleClick)
		}
	}, [handleClick])

	const openMenu = () => {
		setIsOpen(true)
	}

	const closeMenu = () => {
		setIsOpen(false)
	}

	const onClickReward = () => {
		setIsTrialDialogOpen(true)
		localStorage.setItem('hasViewedTrialOffer', 'true')
		setHasViewedTrialOffer(true)
	}

	return (
		<div>
			<TrialOffer
				title='Try Pro Subscription For Free!'
				features='Cloud sandbox to run and test code,Unlock Professional Courses & Topics,Collect Awards and Badges,7 days free trial,...more'
				buttonText='Start Your 7-day Trial'
				isOpenDialog={isTrialDialogOpen}
				onCloseButton={() => setIsTrialDialogOpen(false)}
			/>
			<StyledAppBar>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						width: '100%',
					}}
				>
					{/* Left Container - Logo and Menu Items */}
					<Box sx={{ display: 'flex', alignItems: 'center' }}>
						<Toolbar>
							<AppLogoExternal
								className='app-logo'
								href='https://secdim.com/'
								target='_blank'
							>
								<img src={logo} alt='SecDim' title='SecDim' />
							</AppLogoExternal>
						</Toolbar>
						{smSize ? (
							<MenuIcon className='mobile-menu-icon' onClick={openMenu} />
						) : (
							<Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
								<HeaderLink onClick={() => navigate(generatePath(['/']))}>
									Learn
								</HeaderLink>
								<Box sx={{ mt: 3, ml: 0.35 }} />
								<HeaderExLink href='https://play.secdim.com'>Play</HeaderExLink>
								<Box sx={{ mt: 3, ml: 2.25 }} />
								<HeaderExLink href='https://discuss.secdim.com/'>Discuss</HeaderExLink>
								<Box sx={{ mt: 3, ml: 1.65 }} />
								<HeaderExLink href='https://secdim.com/workshop/'>
									Workshops
								</HeaderExLink>
							</Box>
						)}
					</Box>

					{/* Mobile Menu Drawer - Only visible on mobile */}
					{smSize && (
						<Box
							id='menu-wrap'
							className={isOpen ? 'state-visible' : ''}
							ref={menu}
							sx={{ mt: isOpen ? 0 : 1.2 }}
						>
							<HeaderLink
								onClick={() => {
									closeMenu()
									window.location.href = `${AppRouteNames.home}`
								}}
							>
								Learn
							</HeaderLink>
							<Box sx={{ mt: 3, ml: isOpen ? 0 : 0.35 }} />
							<HeaderExLink href='https://play.secdim.com'>Play</HeaderExLink>
							<Box sx={{ mt: 3, ml: isOpen ? 0 : 2.25 }} />
							<HeaderExLink href='https://discuss.secdim.com/'>Discuss</HeaderExLink>
							<Box sx={{ mt: 3, ml: isOpen ? 0 : 1.65 }} />
							<HeaderExLink href='https://secdim.com/workshop/'>
								Workshops
							</HeaderExLink>
						</Box>
					)}

					{/* Right Container - trial offer, greeting, and Dr. SecDim.
					    Dr. SecDim lives here permanently as a single mounted instance, so
					    crossing the 900px breakpoint never remounts it and an open dropdown
					    survives the switch. Above 900px its wrapper is lifted out of flow and
					    absolutely centred in the header; at/below 900px it sits in-flow as the
					    first control in this cluster, alongside the trial/greeting icons. */}
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								...(dockDrSecDimRight ? {} : CENTERED_SX),
							}}
						>
							<DrSecDim />
						</Box>
						{profile.isTrialAvailable && (
							<Tooltip arrow title='You can claim your free trial from here anytime'>
								<Badge
									color='error'
									variant='dot'
									invisible={hasViewedTrialOffer}
									sx={{ '& .MuiBadge-dot': { right: '12px', top: '5px' } }}
								>
									<Styled.RedeemButton onClick={onClickReward}>
										<Styled.RedeemIcon color='primary' />
									</Styled.RedeemButton>
								</Badge>
							</Tooltip>
						)}
						<Greeting isAnimated={false} />
					</Box>

					<Box
						id='overlay-background'
						className={isOpen ? 'state-visible' : ''}
						onClick={closeMenu}
					/>
				</Box>
			</StyledAppBar>
		</div>
	)
}

export default Header
